import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

export type NotificationKind =
  | "finding-assigned"
  | "mentioned"
  | "regressed"
  | "tracker-conflict";

export type NotificationChannel = "off" | "in-app" | "in-app+email";

export const KIND_DETAILS: Record<
  NotificationKind,
  { label: string; description: string }
> = {
  "finding-assigned": {
    label: "Finding assigned",
    description: "When a canonical finding is assigned to you.",
  },
  mentioned: {
    label: "Mentioned in comment",
    description: "When someone mentions your @name in a finding comment.",
  },
  regressed: {
    label: "Finding regressed",
    description:
      "When a verified finding is observed again in a new walkthrough run.",
  },
  "tracker-conflict": {
    label: "Tracker conflict",
    description:
      "When linked external issues/PRs fall out of sync with finding state.",
  },
};

export const KINDS: NotificationKind[] = [
  "finding-assigned",
  "mentioned",
  "regressed",
  "tracker-conflict",
];

interface NotificationPreferencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationPreferencesDialog({
  open,
  onOpenChange,
}: NotificationPreferencesDialogProps) {
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.notification.preferences.useQuery(
    undefined,
    {
      enabled: open,
    },
  );

  const [channels, setChannels] = useState<
    Record<NotificationKind, NotificationChannel>
  >({
    "finding-assigned": "in-app",
    mentioned: "in-app",
    regressed: "in-app",
    "tracker-conflict": "in-app",
  });

  useEffect(() => {
    if (data?.preferences) {
      const next = { ...channels };
      for (const p of data.preferences) {
        next[p.kind as NotificationKind] = p.channel as NotificationChannel;
      }
      setChannels(next);
    }
  }, [data]);

  const updateMutation = trpc.notification.updatePreferences.useMutation({
    onSuccess: () => {
      toast.success("Notification preferences saved");
      utils.notification.preferences.invalidate();
      onOpenChange(false);
    },
    onError: (err) => {
      toastTrpcError(err, "Failed to save preferences");
    },
  });

  const handleSave = () => {
    const preferences = KINDS.map((kind) => ({
      kind,
      channel: channels[kind],
    }));
    updateMutation.mutate({ preferences });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Notification Preferences</DialogTitle>
          <DialogDescription>
            Choose how you want to be notified for each type of activity.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          {KINDS.map((kind) => {
            const detail = KIND_DETAILS[kind];
            const current = channels[kind];

            return (
              <div
                key={kind}
                className="flex items-center justify-between gap-4 rounded-lg border border-border/60 p-3 bg-muted/20"
              >
                <div className="space-y-0.5 min-w-0 pr-2">
                  <Label className="text-sm font-medium leading-none text-foreground">
                    {detail.label}
                  </Label>
                  <p className="text-xs text-muted-foreground leading-normal mt-1">
                    {detail.description}
                  </p>
                </div>
                <div className="shrink-0 w-[150px]">
                  <Select
                    value={current}
                    onValueChange={(val: NotificationChannel) =>
                      setChannels((prev) => ({ ...prev, [kind]: val }))
                    }
                    disabled={isLoading || updateMutation.isPending}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in-app">In-app only</SelectItem>
                      <SelectItem value="in-app+email">
                        In-app + Email
                      </SelectItem>
                      <SelectItem value="off">Off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
